/**
 * アプリケーション全体で使用するモデル定義
 * GraphQLスキーマに合わせた型定義を集約
 */

/**
 * 会話データの型定義
 */
export interface Conversation {
  conversationId: string;
  sessionId: string;
  counter: number;
  userContent?: string;
  aiContent?: string;
  aiGuide?: string;
  aiParams?: AiParam;
  status: string;
  createdAt: string;
  updatedAt: string;
  userAudioPath?: string;
  aiAudioPath?: string;
  levelScore?: number;
}

/**
 * AIパラメータの型定義
 */
export interface AiParam {
  level: {
    score: string;
    reason: string;
  };
}

/**
 * セッションデータの型定義
 */
export interface Session {
  sessionId: string;
  userId: string;
  scenarioId: string;
  startTime: string;
  endTime?: string;
  status: 'CREATED' | 'IN_PROGRESS' | 'COMPLETED' | 'EVALUATED' | 'EVALUATION_FAILED' | 'EVALUATION_SKIPPED' | 'FAILURE';
  characterSettings?: string;
  aiGuide?: boolean;
  aiSubtitleDisplay?: boolean;
  conversations?: Conversation[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/**
 * ロールプレイキャラクターの性格
 */
export const CharacterSettingTypes = [
  {label: '気さくで話に乗りやすい', value: '気さくで話しやすく、誰とでもすぐに打ち解けるタイプ。新しいものに興味を持ち、前向きな姿勢で物事に取り組む。ユーモアを交えた会話を好む。'},
  {label: 'ぶっきらぼうで関心が低い', value: 'ぶっきらぼうで率直な性格。相手の発言に否定的なところが多々あり、共感力が低く、終始態度が悪い。ただし、すべて否定するのではなく、良いと思ったことに対しては賛同できる。'},
  {label: '慎重で、細かい性格', value: '慎重で細かい性格。決断を下す前に詳細な情報を集め、リスクをしっかりと評価する。文書やデータを重視し、質問が多い。'},
];

/**
 * シナリオデータの型定義
 */
export interface Scenario {
  scenarioId: string;
  companyId: string;
  department?: string;
  title: string;
  description: string;
  goal: string;
  estimatedTime?: number;
  situation: string;
  targetName: string;
  targetGender: string;
  targetAge: string;
  targetLanguage?: string;
  tags?: string[];
  isActive: boolean;
  usageCount: number;
  displayOrder?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  materials?: Material[];
  /** オリエンテーションで「資料」を表示するか（未設定時は表示） */
  showMaterialsOnOrientation?: boolean;
  /** 状況設定（クローズ）：管理者のみ入力、ユーザーには非表示・AIには渡す。2,000文字上限・非必須 */
  situationClosed?: string;
}

// 状況設定テンプレートの型定義
export interface SituationTemplate {
  templateId: string;
  title: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * 資料データの型定義
 */
export type ExtractionStatus = 'PENDING' | 'PROCESSING' | 'COMBINED' | 'COMPLETED' | 'FAILED';

export interface Material {
  materialId: string;
  companyId: string;
  parentScenarioId: string;
  name: string;
  type: string;
  pdfPath?: string;
  extractedText?: string;
  extractionStatus?: ExtractionStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/**
 * カスタム評価ルールデータの型定義
 */
export interface CustomEvaluationRule {
  ruleId: string;
  companyId: string;
  parentScenarioId: string;
  name: string;
  content: string;
  displayOrder?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/**
 * ユーザーデータの型定義
 */
export interface User {
  userId: string;
  givenUserId: string;
  email?: string;
  displayName: string;
  companyId: string;
  department?: string;
  departments?: string[];
  managementScope?: string;
  profileId?: string;
  profile?: Profile | null;
  lastLoginDate?: string;
  disable: boolean;
  locked?: boolean;
  tempPassword?: string;
  userRole: 'USER' | 'ADMIN' | 'SYSTEM_ADMIN';
  createdAt: string;
  updatedAt: string;
}

/**
 * プロファイル権限フラグ
 */
export interface ProfilePermissions {
  userMgmt: {
    view: { canView: boolean; canCsvDownload: boolean };
    edit: { canCreateNew: boolean; canCsvImport: boolean; canBulkUpdate: boolean; canUpdate: boolean; canDelete: boolean; canChangePassword: boolean };
  };
  scenarioMgmt: {
    view: { canView: boolean; canCsvDownload: boolean };
    edit: { canCreateNew: boolean; canCsvImport: boolean; canUpdate: boolean; canDelete: boolean; canCustomScenarioUpdate: boolean };
  };
  analytics: {
    view: { canView: boolean; canCsvDownload: boolean };
  };
  profileMgmt: {
    view: { canView: boolean };
    edit: { canCreateNew: boolean; canUpdate: boolean; canDelete: boolean };
  };
}

/**
 * プロファイル（権限セット）
 */
export interface Profile {
  profileId: string;
  companyId: string;
  name: string;
  description?: string;
  permissions: ProfilePermissions;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

/**
 * 評価データの型定義
 */
export interface Evaluation {
  evaluationId: string;
  sessionId: string;
  userId: string;
  scenarioId: string;
  companyId: string;
  results: string;
  strengths?: string[];
  improvements?: string[];
  advice?: string;
  keyPoints?: string[];
  createdAt: string;
  updatedAt: string;
}
